import React, { useEffect, useMemo } from 'react';
import { Typography, Box, Stack, IconButton, FormControlLabel, Switch } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import Snackbar from '/packages/shared-components/modal/Snackbar/Snackbar';
import list_alt_check from 'url:/packages/shared-components/assets/images/list_alt_check.svg';
import GoldStandardSetCard from '../GoldStandardSetCard/GoldStandardSetCard';
import SkuSelectInput from '../SkuSelectInput/SkuSelectInput';
import { toSkuOptions } from '../SkuSelectInput/skuSelectInputUtils';
import { SkuBoxProps, SkuBoxStates, TOGGLE_LABELS } from '../TestPlanActionsModal.types';
import { getSkuValue } from '../validation.utils';

const SkuBox: React.FC<SkuBoxProps> = ({
  skuId,
  skuFieldName,
  control,
  watch,
  data,
  skuBoxStates,
  setSkuBoxStates,
  goldStandardFormValues,
  setGoldStandardFormValues,
  enableAllSets,
  setEnableAllSets,
  goldStandardError,
  setGoldStandardError,
  allSkuBoxes,
  onRemove,
  activeBoxId,
  setActiveBoxId,
  collapsedState,
  setCollapsedState,
  onUserInteraction,
  loadingSkus = false,
}) => {
  const skuValue = watch(skuFieldName);
  const hasSkuValue = !!getSkuValue(skuValue);
  const isMainBox = skuId === 0;
  const boxKey = isMainBox ? 'main' : `sku_${skuId}`;
  const boxState = skuBoxStates[boxKey] || {};
  const isActive = activeBoxId === boxKey;
  const isCollapsed = boxState.hasError ? false : collapsedState?.[boxKey] || false;

  useEffect(() => {
    if (boxState.hasError && collapsedState?.[boxKey]) {
      setCollapsedState?.({ ...collapsedState, [boxKey]: false });
    }
  }, [boxState.hasError, boxKey, collapsedState, setCollapsedState]);

  useEffect(() => {
    if (skuValue && collapsedState?.[boxKey]) {
      setCollapsedState?.({ ...collapsedState, [boxKey]: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuValue]);

  const handleEnableAllSetsChange = (checked: boolean) => {
    if (isMainBox) {
      setEnableAllSets?.(checked);
    } else {
      setSkuBoxStates((prev: SkuBoxStates) => ({
        ...prev,
        [boxKey]: { ...prev[boxKey], enableAllSets: checked },
      }));
    }
  };

  const handleClearError = (errorType?: 'sets' | 'transitions') => {
    if (!errorType) return;
    if (errorType === 'sets') {
      // Only clear "At least one set needs to be active" errors
      const errorMsg = 'At least one set needs to be active.';
      if (isMainBox) {
        setGoldStandardError?.(false);
        setSkuBoxStates((prev: SkuBoxStates) => ({
          ...prev,
          main: {
            ...prev.main,
            hasError: prev.main?.errorMessage === errorMsg ? false : prev.main?.hasError,
            errorMessage: prev.main?.errorMessage === errorMsg ? '' : prev.main?.errorMessage,
          },
        }));
      } else {
        setSkuBoxStates((prev: SkuBoxStates) => ({
          ...prev,
          [boxKey]: {
            ...prev[boxKey],
            hasError: prev[boxKey]?.errorMessage === errorMsg ? false : prev[boxKey]?.hasError,
            errorMessage: prev[boxKey]?.errorMessage === errorMsg ? '' : prev[boxKey]?.errorMessage,
          },
        }));
      }
    } else if (errorType === 'transitions') {
      // Clear all errors when transitions are valid
      if (isMainBox) {
        setGoldStandardError?.(false);
        setSkuBoxStates((prev: SkuBoxStates) => ({
          ...prev,
          main: { ...prev.main, hasError: false, errorMessage: '' },
        }));
      } else {
        setSkuBoxStates((prev: SkuBoxStates) => ({
          ...prev,
          [boxKey]: { ...prev[boxKey], hasError: false, errorMessage: '' },
        }));
      }
    }
  };

  const handleFormValuesChange = (values: any) => {
    onUserInteraction?.();
    if (isMainBox) {
      setGoldStandardFormValues?.(values);
    } else {
      setSkuBoxStates((prev: SkuBoxStates) => ({
        ...prev,
        [boxKey]: { ...prev[boxKey], formValues: values },
      }));
    }
  };

  const getEnabledToggles = () => {
    const formValues = isMainBox ? goldStandardFormValues || {} : boxState.formValues || {};
    return Object.keys(formValues).filter((key) => {
      const value = formValues[key];
      return typeof value === 'boolean' ? value : value?.offerTransition || value?.tierDowngrade || value?.tierUpgrade;
    });
  };

  const skuOptions = useMemo(
    () => toSkuOptions((data?.skusByProvider ?? []).filter((sku) => sku.planVendingEnabled === true)),
    [data],
  );

  const hasError = isMainBox ? goldStandardError || boxState.hasError : boxState.hasError;
  const currentEnableAllSets = isMainBox ? enableAllSets : boxState.enableAllSets || false;

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${isActive ? theme.palette.text.primary : theme.palette.divider}`,
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        padding: '20px',
        position: 'relative',
      }}
      onClick={() => {
        setActiveBoxId?.(boxKey);
        onUserInteraction?.();
      }}
      onFocusCapture={() => {
        setActiveBoxId?.(boxKey);
        onUserInteraction?.();
      }}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setActiveBoxId?.(null);
        }
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box
          sx={{ flex: 1, minWidth: 0, pt: 1 }}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <SkuSelectInput
            control={control}
            name={skuFieldName}
            options={skuOptions}
            freeSolo
            loading={loadingSkus}
            required
            hideLabel
            dataCy={`sku-autocomplete-${skuFieldName}`}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            pt: 2.5,
          }}
        >
          <FormControlLabel
            sx={{ color: hasSkuValue ? 'text.primary' : 'text.disabled', marginRight: '12px' }}
            control={
              <Switch
                color="success"
                checked={currentEnableAllSets}
                disabled={!hasSkuValue}
                onChange={(e) => handleEnableAllSetsChange(e.target.checked)}
              />
            }
            labelPlacement="start"
            label="Enable All Sets"
          />
          <Stack direction="row">
            {allSkuBoxes && allSkuBoxes.length > 1 && (
              <IconButton
                onClick={() => onRemove?.(skuId)}
                disabled={allSkuBoxes.length <= 1}
                sx={{ p: 0.5, color: 'inherit' }}
              >
                <DeleteOutlineIcon color="inherit" sx={{ fontSize: '20px' }} />
              </IconButton>
            )}
            <Box
              role="button"
              sx={{ cursor: 'pointer', color: hasSkuValue ? 'text.primary' : 'text.disabled', ml: 1 }}
              onClick={() => hasSkuValue && setCollapsedState?.({ ...collapsedState, [boxKey]: !isCollapsed })}
            >
              {hasSkuValue ? isCollapsed ? <ExpandMore /> : <ExpandLess /> : <ExpandMore />}
            </Box>
          </Stack>
        </Box>
      </Stack>

      {hasError && (
        <Snackbar
          content={
            boxState.errorMessage === 'At least one SKU is required for enabled transitions.'
              ? 'At least one SKU is required for enabled transitions.'
              : boxState.errorMessage || 'At least one set needs to be active.'
          }
          status="error"
          variant="subtle"
          sx={{ marginTop: '16px' }}
          data-testid="error-snackbar"
        />
      )}

      {isCollapsed &&
        skuValue &&
        (() => {
          const enabledToggles = getEnabledToggles();
          return (
            enabledToggles.length > 0 && (
              <Stack mt="18px" direction="row">
                <Box
                  component="img"
                  src={list_alt_check}
                  sx={{
                    width: '18px',
                    height: '18px',
                    marginTop: '-3px',
                    filter: (theme) => (theme.palette.mode === 'light' ? 'brightness(0)' : 'brightness(0) invert(1)'),
                  }}
                />
                <Typography variant="caption" sx={{ marginLeft: '8px' }}>
                  {enabledToggles.map((k) => TOGGLE_LABELS[k]).join(', ')}
                </Typography>
              </Stack>
            )
          );
        })()}

      {hasSkuValue && (
        <Box sx={{ display: isCollapsed ? 'none' : 'block' }}>
          <GoldStandardSetCard
            dropdownSkus={skuOptions}
            enableAllSets={currentEnableAllSets}
            onMainToggleChange={handleEnableAllSetsChange}
            hasSkuValue={hasSkuValue}
            hasError={hasError}
            onClearError={handleClearError}
            onFormValuesChange={handleFormValuesChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default SkuBox;
